import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Wordmark } from "../components/Wordmark";
import { AlertCircle, CheckCircle2, MailCheck } from "lucide-react";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (e2) { setErr(apiError(e2)); }
  };
  return (
    <Shell>
      {sent ? (
        <div className="space-y-3 text-center" data-testid="forgot-sent"><MailCheck className="mx-auto h-12 w-12 text-emerald-600" /><h1 className="font-head text-2xl font-bold">Check your email</h1><p className="text-sm text-muted-foreground">If <b>{email}</b> has an account, a reset link is on its way. It expires in 1 hour.</p><Button asChild variant="outline" className="rounded-full"><Link to="/login">Back to sign in</Link></Button></div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <h1 className="font-head text-2xl font-bold tracking-tight">Forgot your password?</h1>
          <p className="text-sm text-muted-foreground">Enter your work email and we'll send you a secure link to choose a new one.</p>
          {err && <p className="flex items-center gap-2 text-sm text-destructive" data-testid="forgot-error"><AlertCircle className="h-4 w-4" />{err}</p>}
          <div className="space-y-2"><Label htmlFor="fe">Work email</Label><Input id="fe" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="forgot-email" /></div>
          <Button type="submit" className="h-11 w-full rounded-full" data-testid="forgot-submit">Send reset link</Button>
          <Link to="/login" className="block text-center text-sm text-muted-foreground hover:text-foreground">Back to sign in</Link>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState(""); const [pw2, setPw2] = useState("");
  const [err, setErr] = useState(""); const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pw !== pw2) return setErr("Passwords don't match.");
    try { await api.post("/auth/reset-password", { token: params.get("token"), password: pw }); setDone(true); setTimeout(() => navigate("/login"), 2500); } catch (e2) { setErr(apiError(e2)); }
  };
  return (
    <Shell>
      {done ? (
        <div className="space-y-3 text-center" data-testid="reset-done"><CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" /><h1 className="font-head text-2xl font-bold">Password updated</h1><p className="text-sm text-muted-foreground">Taking you to sign in…</p></div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <h1 className="font-head text-2xl font-bold tracking-tight">Choose a new password</h1>
          <p className="text-sm text-muted-foreground">Your email stays your username. Pick a password of at least 10 characters.</p>
          {err && <p className="flex items-center gap-2 text-sm text-destructive" data-testid="reset-error"><AlertCircle className="h-4 w-4" />{err}</p>}
          <div className="space-y-2"><Label htmlFor="p1">New password</Label><Input id="p1" type="password" required minLength={10} value={pw} onChange={(e) => setPw(e.target.value)} className="h-11" data-testid="reset-password" autoComplete="new-password" /></div>
          <div className="space-y-2"><Label htmlFor="p2">Confirm password</Label><Input id="p2" type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} className="h-11" data-testid="reset-password2" autoComplete="new-password" /></div>
          <Button type="submit" className="h-11 w-full rounded-full" data-testid="reset-submit">Save new password</Button>
        </form>
      )}
    </Shell>
  );
}

export function ResetAuthenticator() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [email, setEmail] = useState(""); const [pw, setPw] = useState("");
  const [err, setErr] = useState(""); const [sent, setSent] = useState(false); const [state, setState] = useState(token ? "confirming" : "form");
  useEffect(() => {
    if (!token) return;
    api.post("/auth/confirm-authenticator-reset", { token }).then(() => { setState("done"); setTimeout(() => navigate("/login"), 3000); }).catch((e) => { setErr(apiError(e)); setState("form"); });
  }, [token]); // eslint-disable-line
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { await api.post("/auth/request-authenticator-reset", { email, password: pw }); setSent(true); } catch (e2) { setErr(apiError(e2)); }
  };
  return (
    <Shell>
      {state === "confirming" ? <p className="text-sm text-muted-foreground" data-testid="authreset-confirming">Resetting your authenticator…</p>
        : state === "done" ? (
          <div className="space-y-3 text-center" data-testid="authreset-done"><CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" /><h1 className="font-head text-2xl font-bold">Authenticator reset</h1><p className="text-sm text-muted-foreground">Delete the old Airfistuus entry in Google Authenticator, then sign in to scan a new QR code. Taking you to sign in…</p></div>
        ) : sent ? (
          <div className="space-y-3 text-center" data-testid="authreset-sent"><CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" /><h1 className="font-head text-2xl font-bold">Check your email</h1><p className="text-sm text-muted-foreground">If the email and password match an account, we sent a link that resets your authenticator. It works once and expires in 1 hour.</p></div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <h1 className="font-head text-2xl font-bold tracking-tight">Reset your authenticator</h1>
            <p className="text-sm text-muted-foreground">Lost your phone or codes keep getting rejected? Confirm your email and password and we'll email you a link to re-enroll with a new QR code.</p>
            {err && <p className="flex items-center gap-2 text-sm text-destructive" data-testid="authreset-error"><AlertCircle className="h-4 w-4" />{err}</p>}
            <div className="space-y-2"><Label htmlFor="ae">Email</Label><Input id="ae" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="authreset-email" autoComplete="email" /></div>
            <div className="space-y-2"><Label htmlFor="ap">Password</Label><Input id="ap" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} className="h-11" data-testid="authreset-password" autoComplete="current-password" /></div>
            <Button type="submit" className="h-11 w-full rounded-full" data-testid="authreset-submit">Email me a reset link</Button>
            <Link to="/login" className="block text-center text-sm text-muted-foreground hover:underline">Back to sign in</Link>
          </form>
        )}
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="flex min-h-screen items-center justify-center bg-background p-4">
    <Card className="w-full max-w-md rounded-3xl p-8"><div className="mb-6"><Wordmark className="text-2xl" /></div>{children}</Card>
  </div>
);
